/**
 * 建筑外立面样式：把「重点楼」保留原色，其余楼统一做成白色半透明的体块。
 *
 * ⚠️ 不要在这里加"风格化"（降饱和 / 提亮 / 按几何自动给屋顶上蓝红色）。试过一整轮，
 * 结论是**不好看**：这批 FBX 的照片贴图是客户认出自家楼的依据，糊成浅色体块反而丢了
 * 识别度（用户 2026-09-30 明确否掉）。要改建筑本身的观感，请动模型资产。
 *
 * 背景：每个分片的 GLB 是**一整块合并网格**（例如 part1 的 17 栋楼全在同一个 mesh 里），
 * 没法靠材质直接按楼分色。所以做法是：
 *   1. 用各分片 scenetree 的元素球心（ECEF → 分片局部 ENU）做"最近元素"归属，
 *      给每个顶点写一个属性 aKeep：1 = 这栋楼要保留原色，0 = 做成白色半透明；
 *   2. 给材质打 onBeforeCompile 补丁，按 aKeep 混合颜色与不透明度。
 * 这样不复制几何、不改模型数据，调整样式只动本文件。
 */

/**
 * 「重点楼」楼号（scenetree 里的 name 字段）。
 * 这些楼保留原始材质；其余全部变白色半透明。
 */
export const OPAQUE_CODES = ['1', '2', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23']

/** 半透明体块的样式参数 */
export const FACADE_STYLE = {
  // 体块基调色（重点楼不受影响，保留原色）。灰蓝色
  // tintColor: 0x7d95ae,
  tintColor: 0x000000,
  // 基调色占比：0 = 完全保留原材质，1 = 完全是基调色。0.86 让灰蓝更干净、少受原深色材质影响
  tintStrength: 0.86,
  // 半透明楼体的不透明度。0.5 左右：能透出后面的体块层次，又不至于糊掉
  alpha: 0.5,
  // 半透明楼体是否双面可见（玻璃体块观感更完整；关掉更接近实体白模）
  doubleSide: true,
}

/** 该楼号是否要保留原色 */
export function isOpaqueCode(code) {
  if (code === undefined || code === null) return false
  return OPAQUE_CODES.indexOf(String(code)) >= 0
}

/**
 * 编号楼（中心这 21 幢）的自发光。用户 2026-10-22："中心 21 幢建筑物加上自发光效果"。
 *
 * 走材质自己的 emissive 槽，并且 **emissiveMap 复用它的 diffuse 贴图** —— 发光是"按贴图"
 * 发出来的：窗格、雨棚、墙面脏迹原样带出来，不会把立面糊成一层平板光（那才是真正毁
 * 识别度的做法，见上面那段"不要着色器风格化"）。
 *
 * ⚠️ strength 是**线性值**，而 Bloom 阈值（sceneView 的 BLOOM_THRESHOLD = 1.55）比的也是
 * 线性值：最白的贴图乘上它也只有 0.34，离阈值还差一截，所以它只提"楼自己在发光"，
 * 不会把立面推进辉光里炸成白坑（那条约束见 environment.js 的 LIGHT_POOL_OPACITY 注释）。
 * 颜色走"蓝白"：g−r(0.043) > b−g(0.039)，不是被否掉的那种蓝紫。
 */
export const FOCUS_EMISSIVE = { color: 0xdfeaf4, strength: 0.34 }

/** 是否需要做外立面处理（列表为空就完全不动模型） */
export function facadeStyleEnabled() {
  return OPAQUE_CODES.length > 0 || FACADE_STYLE.tintStrength > 0 || FACADE_STYLE.alpha < 1
}
